// src/components/Navigation.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

function Navigation({ userInfo, onLogout }) {
  const navigate = useNavigate();
  const isAdmin = userInfo && (userInfo.role === 'admin' || userInfo.role === 'super_admin');
  const isSuperAdmin = userInfo && userInfo.role === 'super_admin';
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [mobileCompanyLogo, setMobileCompanyLogo] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setIsMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 企業ロゴを取得
  useEffect(() => {
    console.log('Navigation userInfo:', userInfo);
    
    const fetchCompanyLogo = async () => {
      if (!userInfo || !userInfo.companyId) {
        console.error('No userInfo or companyId available');
        return;
      }
      
      try {
        console.log('Fetching logo for company ID:', userInfo.companyId);
        
        // 通常ロゴのURLを設定（直接URLを使用）
        const logoUrl = `/api/company-logo/${userInfo.companyId}?t=${new Date().getTime()}`;
        console.log('Setting logo URL:', logoUrl);
        setCompanyLogo(logoUrl);
        
        // モバイルロゴのURLを設定（直接URLを使用）
        const mobileLogoUrl = `/api/mobile-logo/${userInfo.companyId}?t=${new Date().getTime()}`;
        console.log('Setting mobile logo URL:', mobileLogoUrl);
        setMobileCompanyLogo(mobileLogoUrl);
        
        // モバイルロゴの存在確認（プリロード）
        const img = new Image();
        img.onload = () => {
          console.log('Mobile logo preloaded successfully');
        };
        img.onerror = (e) => {
          console.warn('Mobile logo could not be preloaded, falling back to regular logo', e);
          setMobileCompanyLogo(logoUrl); // 通常のロゴをフォールバックとして使用
        };
        img.src = mobileLogoUrl;
      } catch (error) {
        console.error('Error setting company logo:', error);
        // エラー時は通常のロゴを使用
        const logoUrl = `/api/company-logo/${userInfo.companyId}?t=${new Date().getTime()}`;
        setCompanyLogo(logoUrl);
        setMobileCompanyLogo(logoUrl);
      }
    };
    
    fetchCompanyLogo();
  }, [userInfo]);

  const handleLogout = () => {
    onLogout();
    navigate('/login');
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <nav className="bg-white shadow-md p-4">
      <div className="container mx-auto">
        {/* デスクトップ表示 */}
        {!isMobile && (
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              {companyLogo && (
                <img
                  src={companyLogo}
                  alt="企業ロゴ"
                  className="mr-4"
                  style={{ maxWidth: '128px', maxHeight: '40px', display: 'block', width: 'auto', height: 'auto' }}
                  onLoad={(e) => {
                    console.log('Logo loaded successfully:', companyLogo);
                    e.target.style.display = 'block';
                  }}
                  onError={(e) => {
                    console.error('Error loading logo image:', companyLogo);
                    e.target.style.display = 'none';
                  }}
                />
              )}
              <Link 
                to="/time-clock"
                className="text-gray-700 hover:text-gray-900 px-3 py-2 rounded"
              >
                打刻
              </Link>
              <Link 
                to="/attendance"
                className="text-gray-700 hover:text-gray-900 px-3 py-2 rounded"
              >
                打刻実績
              </Link>
              <Link 
                to="/shift-manager"
                className="text-gray-700 hover:text-gray-900 px-3 py-2 rounded"
              >
                シフト管理
              </Link>
              {isAdmin && (
                <Link 
                  to="/admin"
                  className="text-blue-600 hover:text-blue-800 px-3 py-2 rounded font-semibold"
                >
                  勤怠管理
                </Link>
              )}
              {isSuperAdmin && (
                <Link 
                  to="/super-admin"
                  className="text-red-600 hover:text-red-800 px-3 py-2 rounded font-semibold"
                >
                  管理者ページ
                </Link>
              )}
            </div>
            <div className="flex items-center">
              {userInfo && (
                <span className="mr-4 text-gray-700">
                  {userInfo.name}
                  {userInfo.role === 'super_admin' && <span className="ml-2 text-xs bg-red-500 text-white px-2 py-1 rounded">スーパー管理者</span>}
                  {userInfo.role === 'admin' && <span className="ml-2 text-xs bg-blue-500 text-white px-2 py-1 rounded">管理者</span>}
                </span>
              )}
              <button
                onClick={handleLogout}
                className="text-gray-700 hover:text-gray-900 px-3 py-2 rounded"
              >
                ログアウト
              </button>
            </div>
          </div>
        )}

        {/* モバイル表示 */}
        {isMobile && (
          <div>
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                {mobileCompanyLogo && (
                  <img
                    src={mobileCompanyLogo}
                    alt="企業ロゴ"
                    className="mr-2"
                    style={{ 
                      maxWidth: '40px', 
                      maxHeight: '40px', 
                      width: 'auto', 
                      height: 'auto', 
                      display: 'block',
                      objectFit: 'contain'
                    }}
                    onLoad={(e) => {
                      console.log('Mobile logo loaded successfully:', mobileCompanyLogo);
                      e.target.style.display = 'block';
                    }}
                    onError={(e) => {
                      console.error('Mobile error loading logo image:', mobileCompanyLogo);
                      // モバイルロゴが読み込めない場合、通常のロゴを試す
                      if (mobileCompanyLogo !== companyLogo && companyLogo) {
                        console.log('Falling back to regular logo');
                        e.target.src = companyLogo;
                      } else {
                        e.target.style.display = 'none';
                      }
                    }}
                  />
                )}
                <div className="text-lg font-bold">勤怠管理</div>
              </div>
              <div className="flex items-center">
                {userInfo && (
                  <span className="mr-2 text-sm text-gray-700">
                    {userInfo.name}
                  </span>
                )}
                <button
                  onClick={toggleMenu}
                  className="p-2 focus:outline-none"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    {isMenuOpen ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path>
                    )}
                  </svg>
                </button>
              </div>
            </div>

            {isMenuOpen && (
              <div className="mt-4 flex flex-col space-y-2">
                <Link 
                  to="/time-clock"
                  className="text-gray-700 hover:bg-gray-100 px-3 py-2 rounded"
                  onClick={() => setIsMenuOpen(false)}
                >
                  打刻
                </Link>
                <Link 
                  to="/attendance"
                  className="text-gray-700 hover:bg-gray-100 px-3 py-2 rounded"
                  onClick={() => setIsMenuOpen(false)}
                >
                  打刻実績
                </Link>
                <Link 
                  to="/shift-manager"
                  className="text-gray-700 hover:bg-gray-100 px-3 py-2 rounded"
                  onClick={() => setIsMenuOpen(false)}
                >
                  シフト管理
                </Link>
                {isAdmin && (
                  <Link 
                    to="/admin"
                    className="text-blue-600 hover:bg-blue-50 px-3 py-2 rounded font-semibold"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    勤怠管理
                  </Link>
                )}
                {isSuperAdmin && (
                  <Link 
                    to="/super-admin"
                    className="text-red-600 hover:bg-red-50 px-3 py-2 rounded font-semibold"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    管理者ページ
                  </Link>
                )}
                <button
                  onClick={() => {
                    handleLogout();
                    setIsMenuOpen(false);
                  }}
                  className="text-gray-700 hover:bg-gray-100 px-3 py-2 rounded text-left"
                >
                  ログアウト
                </button>
                {userInfo && userInfo.role === 'super_admin' && (
                  <div className="px-3 py-1">
                    <span className="text-xs bg-red-500 text-white px-2 py-1 rounded">スーパー管理者</span>
                  </div>
                )}
                {userInfo && userInfo.role === 'admin' && (
                  <div className="px-3 py-1">
                    <span className="text-xs bg-blue-500 text-white px-2 py-1 rounded">管理者</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}

export default Navigation;